"use client";

import { useEffect, useState } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useRouter, useSearchParams } from "next/navigation";
import { Bot, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { CommandAgentCard } from "@/components/command-team/command-agent/cards/command-agent-card";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { PermissionGate } from "@/components/shared/permission-gate";
import TablePagination from "@/components/shared/table-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/ui/loader";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { CommandAgent, CommandAgentList } from "@/types/command-agent";
import { del, fetchData, patch, post } from "@/utils/api-handler";
import {
    DEFAULT_TABLE_PAGE_LIMIT,
    DEFAULT_TABLE_SORT_BY_CREATED_AT,
    SEARCH_DEBOUNCE_WAIT,
} from "@/utils/global";

interface CommandAgentsListProps {
    onCreateClick: () => void;
    /** Defaults to `/workforce/command-team-old/agents` (v1). Pass v2 base for Command Team v2 routes. */
    agentDetailBasePath?: string;
    showActiveToggle?: boolean;
}

export function CommandAgentsList({
    onCreateClick,
    agentDetailBasePath = "/workforce/command-team-old/agents",
    showActiveToggle = true,
}: CommandAgentsListProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const queryClient = useQueryClient();

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
    const [currentPage, setCurrentPage] = useState(
        () => parseInt(searchParams.get("page") || "1", 10) || 1
    );
    const [sortBy] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy);
    const [sortOrder] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortOrder);
    const [deletingAgent, setDeletingAgent] = useState<CommandAgent | null>(null);
    const [togglingAgentId, setTogglingAgentId] = useState<string | null>(null);

    const { data, isLoading } = useProtectedQuery<CommandAgentList>({
        queryKey: ["command-agents", debouncedSearch, currentPage, sortBy, sortOrder],
        queryFn: () => {
            const params = new URLSearchParams({
                search: debouncedSearch,
                page: currentPage.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
                limit: DEFAULT_TABLE_PAGE_LIMIT.toString(),
            });

            return fetchData(`/api/command-team/agents?${params.toString()}`);
        },
        requiredPermissions: ["LIST_COMMAND_AGENTS"],
    });

    const deleteMutation = useProtectedMutation({
        mutationFn: (agentId: string) => del(`/api/command-team/agents/${agentId}`),
        requiredPermissions: ["DELETE_COMMAND_AGENT"],
        onSuccess: (_response, agentId) => {
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            setDeletingAgent((current) => {
                const name = current?.name ?? "Agent";
                toast.success("Agent deleted", {
                    description: `"${name}" was removed.`,
                });
                return null;
            });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            toast.error("Failed to delete command agent", {
                description:
                    error.response?.data?.message ||
                    "An error occurred while deleting the command agent.",
            });
        },
    });

    const toggleActiveMutation = useProtectedMutation({
        mutationFn: async ({ agentId, isActive }: { agentId: string; isActive: boolean }) => {
            if (isActive) {
                return post(`/api/command-team/agents/${agentId}/activate`);
            }
            return patch(`/api/command-team/agents/${agentId}`, { is_active: false });
        },
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onMutate: async ({ agentId, isActive }) => {
            setTogglingAgentId(agentId);

            if (!isActive) return;

            await queryClient.cancelQueries({ queryKey: ["command-agents"] });
            const previousListData = queryClient.getQueriesData<CommandAgentList>({
                queryKey: ["command-agents"],
            });

            queryClient.setQueriesData<CommandAgentList>(
                { queryKey: ["command-agents"] },
                (old) => {
                    if (!old?.items) return old;
                    return {
                        ...old,
                        items: old.items.map((item) => ({
                            ...item,
                            is_active: item.id === agentId,
                        })),
                    };
                }
            );

            return { previousListData };
        },
        onSuccess: (_response, { isActive, agentId }) => {
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            queryClient.invalidateQueries({ queryKey: ["command-agent", agentId] });
            if (isActive) {
                queryClient.invalidateQueries({ queryKey: ["command-sessions", agentId] });
            }

            const agent = data?.items.find((item) => item.id === agentId);
            toast.success(isActive ? "Agent activated" : "Agent paused", {
                description: isActive
                    ? `"${agent?.name ?? "Agent"}" is now active and ready to chat.`
                    : `"${agent?.name ?? "Agent"}" will stop responding until reactivated.`,
            });
        },
        onError: (
            error: { response?: { data?: { message?: string } } },
            _variables,
            context?: { previousListData?: Array<[unknown, CommandAgentList | undefined]> }
        ) => {
            context?.previousListData?.forEach(([queryKey, cached]) => {
                queryClient.setQueryData(queryKey as string[], cached);
            });
            toast.error("Failed to update agent status", {
                description:
                    error.response?.data?.message ||
                    "An error occurred while updating the command agent.",
            });
        },
        onSettled: () => {
            setTogglingAgentId(null);
        },
    });

    useEffect(() => {
        if (data && currentPage > data.page_count) {
            setCurrentPage(1);
            const params = new URLSearchParams(searchParams.toString());
            params.set("page", "1");
            router.replace(`${window.location.pathname}?${params.toString()}`);
        }
    }, [data, currentPage, router, searchParams]);

    const openAgent = (agent: CommandAgent) => {
        router.push(
            `${agentDetailBasePath}/${agent.id}?name=${encodeURIComponent(agent.name || "")}`
        );
    };

    const handleToggleActive = (agent: CommandAgent) => {
        toggleActiveMutation.mutate({
            agentId: agent.id,
            isActive: !agent.is_active,
        });
    };

    const agents = data?.items ?? [];

    return (
        <>
            <div className="mb-4">
                <Input
                    className="w-full sm:max-w-[280px]"
                    placeholder="Search agents..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
                />
            </div>

            <div className="relative min-h-[240px]">
                {isLoading ? <Loader /> : null}

                {!isLoading && agents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border py-16 text-center">
                        <div className="flex size-12 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
                            <Bot className="size-6 text-primary" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold">No Command agents yet</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Create your first digital worker to power Command chat.
                            </p>
                        </div>
                        <PermissionGate requiredPermissions={["CREATE_COMMAND_AGENT"]}>
                            <Button
                                onClick={onCreateClick}
                                className="mt-1 h-9 gap-1.5 rounded-full bg-primary px-4 text-sm"
                            >
                                <Plus className="size-4" />
                                New Command Agent
                            </Button>
                        </PermissionGate>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {agents.map((agent) => (
                            <CommandAgentCard
                                key={agent.id}
                                agent={agent}
                                onOpen={() => openAgent(agent)}
                                onToggleActive={() => handleToggleActive(agent)}
                                onDelete={() => setDeletingAgent(agent)}
                                isTogglePending={
                                    togglingAgentId === agent.id && toggleActiveMutation.isPending
                                }
                                showActiveToggle={showActiveToggle}
                            />
                        ))}
                    </div>
                )}
            </div>

            {agents.length > 0 ? (
                <TablePagination
                    currentPage={currentPage}
                    pageCount={data?.page_count || 0}
                    onPageChange={setCurrentPage}
                    pageParamName="page"
                />
            ) : null}

            <DeleteConfirmationDialog
                open={deletingAgent !== null}
                onOpenChange={(open) => {
                    if (!open) setDeletingAgent(null);
                }}
                onConfirm={() => {
                    if (!deletingAgent) return;
                    deleteMutation.mutate(deletingAgent.id);
                }}
                isLoading={deleteMutation.isPending}
                itemName={deletingAgent?.name}
                itemType="Command agent"
            />
        </>
    );
}
